import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import { AnalyzeAPI, Ai360UserAPI } from "@ai360/core";
import { DialogBox, DialogBoxFooterType, Loader } from "~/core";
import { IMultiActionList } from "~/core/components/dialog-box/dialog-box";
import MarkdownRenderer from "~/analyze/components/markdownrenderer";
import { getLoginGuid, getTheUserGuid } from "~/login/selectors";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import { messages } from "~/reports/i18n-messages";
import { logFirebaseEvent } from "~/utils/firebase";

import "./summarize-modal.css";

export interface ReportFileReference {
    FileName: string;
    UserGuid: string;
    ReportGuid: string;
}

interface ParsedSummaryChunks {
    text: string;
    pdf: string | null;
    error: string | null;
}

function parseSummaryChunks(raw: string): ParsedSummaryChunks {
    if (raw.trimStart().startsWith("Error")) {
        return { text: "", pdf: null, error: raw.trim() };
    }

    const lines = raw
        .split(/\n|\}\{/)
        .filter((line) => line.trim() !== "")
        .map((line, i, arr) => {
            const trimmed = line.trim();
            if (i < arr.length - 1 && !trimmed.endsWith("}") && i > 0 && !trimmed.startsWith("{"))
                return "{" + trimmed + "}";
            if (i < arr.length - 1 && !trimmed.endsWith("}")) return trimmed + "}";
            if (i > 0 && !trimmed.startsWith("{")) return "{" + trimmed;
            return trimmed;
        });

    let text = "";
    let pdf: string | null = null;
    let error: string | null = null;

    for (const line of lines) {
        try {
            const parsed = JSON.parse(line);

            switch (parsed.type) {
                case "content_block_delta":
                    if (parsed.delta?.type === "text_delta") {
                        text += parsed.delta.text;
                    }
                    break;
                case "pdf":
                    pdf = parsed.pdf;
                    break;
                case "error":
                    // Multiple independent errors can arrive in one
                    // response (e.g. a failed fetch AND an image-limit
                    // skip) — accumulate them instead of the last one
                    // winning, so every reason still surfaces.
                    error = error ? `${error}\n\n${parsed.message}` : parsed.message;
                    break;
            }
        } catch {
            // Partial JSON line, skip and wait for next chunk
        }
    }

    if (text.trimStart().startsWith("Error")) {
        error = text.trim();
        text = "";
    }

    return { text, pdf, error };
}

interface ParsedErrorMessage {
    paragraphs: string[];
    items: string[];
}

function parseErrorMessage(message: string): ParsedErrorMessage {
    const paragraphs: string[] = [];
    const items: string[] = [];

    // A blank line separates independent error messages (see the
    // "error" accumulation in parseSummaryChunks) — each becomes its own
    // paragraph rather than being run together, while every "- " bullet
    // across all of them merges into one combined list.
    message.split(/\n\s*\n/).forEach((block) => {
        const headerLines: string[] = [];
        block
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line !== "")
            .forEach((line) => {
                if (line.startsWith("- ")) {
                    items.push(line.slice(2));
                } else {
                    headerLines.push(line);
                }
            });
        if (headerLines.length > 0) {
            paragraphs.push(headerLines.join(" "));
        }
    });

    return { paragraphs, items };
}

export interface SummarizeModalProps {
    isOpen: boolean;
    reportFiles: ReportFileReference[];
    numberOfReports: number;
    onClose: () => void;
}

export function SummarizeModal({
    isOpen,
    reportFiles,
    numberOfReports,
    onClose,
}: SummarizeModalProps) {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const loginGuid = useSelector(getLoginGuid);
    const userGuid = useSelector(getTheUserGuid);

    const { data: creditData } = Ai360UserAPI.useCheckBedrockCreditQuery(
        { loginGuid, userGuid },
        { skip: !loginGuid || !userGuid, refetchOnMountOrArgChange: true }
    );

    const [rawOutput, setRawOutput] = useState("");
    const [textOutput, setTextOutput] = useState("");
    const [pdfOutput, setPdfOutput] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [_isStreaming, setIsStreaming] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const abortControllerRef = useRef<AbortController | null>(null);
    const rawOutputRef = useRef("");
    const prevIsOpenRef = useRef(false);
    const hasLoggedCompletionRef = useRef(false);

    useEffect(() => {
        if (!rawOutput) {
            return;
        }

        const { text, pdf, error } = parseSummaryChunks(rawOutput);

        // error and text are NOT mutually exclusive — a partial skip
        // (e.g. one report over the image limit) sends an error banner
        // while the rest of the selection still streams a real summary.
        // Both render independently below, so both get set here.
        setErrorMessage(error);
        setTextOutput(text);
        if (pdf) {
            setPdfOutput(pdf);
        }
        if (text !== "" || error) {
            setIsLoading(false);
        }
    }, [rawOutput]);

    useEffect(() => {
        // Gated on textOutput alone, not errorMessage — a partial skip
        // (error banner + real summary) still produced a real summary
        // and should count as a completed run, not be silently excluded
        // from usage tracking just because an error banner also showed.
        if (isLoading || hasLoggedCompletionRef.current || !textOutput) {
            return;
        }
        hasLoggedCompletionRef.current = true;
        logFirebaseEvent("summarize_selected_reports", { numberOfReports });
    }, [isLoading, textOutput, numberOfReports]);

    useLayoutEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            startStreaming();
        }

        if (!isOpen && prevIsOpenRef.current) {
            abortStream();
            resetState();
        }

        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            abortStream();
        };
    }, []);

    const startStreaming = async () => {
        rawOutputRef.current = "";
        setRawOutput("");
        setTextOutput("");
        setPdfOutput(null);
        setErrorMessage(null);
        setIsLoading(true);
        setIsStreaming(true);
        hasLoggedCompletionRef.current = false;

        abortControllerRef.current = new AbortController();

        let fallbackError: string | null = null;

        try {
            await AnalyzeAPI.summarizeReports(
                reportFiles,
                loginGuid ?? "",
                userGuid ?? "",
                (chunk: string) => {
                    rawOutputRef.current += chunk;
                    setRawOutput((prev) => prev + chunk);
                },
                () => setIsStreaming(false)
            );

            const { text, error } = parseSummaryChunks(rawOutputRef.current);
            if (!text && !error) {
                fallbackError = formatMessage(messages.summarizeError);
            }
        } catch (error) {
            if (error?.name === "AbortError") {
                return;
            }
            fallbackError = formatMessage(messages.summarizeError);
        }

        setIsStreaming(false);
        setIsLoading(false);
        if (fallbackError) {
            setErrorMessage(fallbackError);
        }
        if (loginGuid && userGuid) {
            dispatch(
                Ai360UserAPI.endpoints.checkBedrockCredit.initiate(
                    { loginGuid, userGuid },
                    { subscribe: false, forceRefetch: true }
                )
            );
        }
    };

    const abortStream = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    };

    const resetState = () => {
        setRawOutput("");
        setTextOutput("");
        setPdfOutput(null);
        setIsLoading(false);
        setIsStreaming(false);
        setErrorMessage(null);
    };

    const handleClose = () => {
        abortStream();
        onClose();
    };

    const handlePrint = () => {
        try {
            if (!pdfOutput) {
                dispatch(
                    notificationActions.pushToasterMessage(
                        messages.summarizePrintError,
                        MSGTYPE.ERROR
                    )
                );
                return;
            }

            logFirebaseEvent("summarize_selected_reports_print", { numberOfReports });

            const date = new Date()
                .toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                })
                .replace(/\//g, "-");

            const randomNum = String(Math.floor(Math.random() * 1000000)).padStart(6, "0");

            const fileName = `SummarizeReports_${date}_${randomNum}`;

            const linkSource = `data:application/pdf;base64,${pdfOutput}`;
            const downloadLink = document.createElement("a");

            downloadLink.href = linkSource;
            downloadLink.download = fileName;
            downloadLink.click();
        } catch {
            dispatch(
                notificationActions.pushToasterMessage(messages.summarizePrintError, MSGTYPE.ERROR)
            );
        }
    };

    const escapeHtmlChars = (text: string): string => {
        const escapeMap: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;" };
        return text.replace(/[&<>]/g, (match) => escapeMap[match]);
    };

    const convertHeadings = (html: string): string => {
        return html
            .replace(/^### (.+)$/gm, "<h3>$1</h3>")
            .replace(/^## (.+)$/gm, "<h2>$1</h2>")
            .replace(/^# (.+)$/gm, "<h1>$1</h1>");
    };

    const convertTables = (html: string): string => {
        return html.replace(/^\|(.+)\|\n\|[\s-|:]+\|\n((?:\|.+\|\n)*)/gm, (match) => {
            const lines = match.split("\n").filter((line) => line.trim());
            if (lines.length < 2) return match;

            const [headerLine, ...bodyLines] = lines;
            const headers = headerLine
                .split("|")
                .map((cell) => cell.trim())
                .filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1)
                .map((h) => `<th>${h}</th>`)
                .join("");

            const rows = bodyLines
                .map((line) =>
                    line
                        .split("|")
                        .map((cell) => cell.trim())
                        .filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1)
                        .map((cell) => `<td>${cell}</td>`)
                        .join("")
                )
                .map((row) => `<tr>${row}</tr>`)
                .join("");

            return `<table border="1" style="border-collapse:collapse;"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
        });
    };

    const convertCodeBlocks = (html: string): string => {
        return html.replace(/```[\s\S]*?```/g, (match) => {
            const code = match.replace(/^```[\w]*\n?/, "").replace(/```$/, "");
            return `<pre><code>${code}</code></pre>`;
        });
    };

    const convertBlockElements = (html: string): string => {
        return html
            .replace(/^&gt; (.+)$/gm, "<blockquote>$1</blockquote>")
            .replace(/^[*-] (.+)$/gm, "<li>$1</li>")
            .replace(/(<li>.*<\/li>)/s, (match) => `<ul>${match}</ul>`)
            .replace(/^\d+\. (.+)$/gm, "<li>$1</li>")
            .replace(/^---+$/gm, "<hr>");
    };

    const convertInlineFormatting = (html: string): string => {
        return html
            .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
            .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
            .replace(/\*(.+?)\*/g, "<em>$1</em>")
            .replace(/`(.+?)`/g, "<code>$1</code>")
            .replace(/~~(.+?)~~/g, "<del>$1</del>")
            .replace(
                /\[(.+?)\]\((.+?)\)/g,
                (_match, label, href) => `<a href="${href.replace(/&amp;/g, "&")}">${label}</a>`
            );
    };

    const wrapParagraphs = (html: string): string => {
        return html.replace(/^([^<][^\n]*?)$/gm, (match) => {
            if (
                match.startsWith("<") ||
                match.trim() === "" ||
                match.startsWith("<li>") ||
                match.startsWith("<blockquote>")
            ) {
                return match;
            }
            return `<p>${match}</p>`;
        });
    };

    const markdownToHtml = (md: string): string => {
        let html = escapeHtmlChars(md);
        html = convertHeadings(html);
        html = convertTables(html);
        html = convertCodeBlocks(html);
        html = convertBlockElements(html);
        html = convertInlineFormatting(html);
        html = wrapParagraphs(html);
        html = html.replace(/<p><\/p>/g, "").replace(/\n/g, "");
        return html;
    };

    const markdownToPlainText = (markdown: string): string => {
        return markdown
            .replace(/\*\*\*(.+?)\*\*\*/g, "$1")
            .replace(/\*\*(.+?)\*\*/g, "$1")
            .replace(/\*(.+?)\*/g, "$1")
            .replace(/~~(.+?)~~/g, "$1")
            .replace(/`(.+?)`/g, "$1")
            .replace(/\[(.+?)\]\(.+?\)/g, "$1")
            .replace(/^#+\s/gm, "")
            .replace(/^\s*[*-]\s/gm, "")
            .replace(/^\s*\d+\.\s/gm, "")
            .replace(/^>\s/gm, "");
    };

    const copyFormattedContent = async (htmlContent: string, plainText: string): Promise<void> => {
        const blob = new Blob([htmlContent], { type: "text/html" });
        const html = new ClipboardItem({
            "text/html": blob,
            "text/plain": new Blob([plainText], { type: "text/plain" }),
        });

        try {
            await navigator.clipboard.write([html]);
        } catch {
            await navigator.clipboard.writeText(plainText);
        }
    };

    const handleCopy = async () => {
        try {
            const htmlContent = markdownToHtml(textOutput);
            const plainText = markdownToPlainText(textOutput);

            await copyFormattedContent(htmlContent, plainText);

            logFirebaseEvent("summarize_selected_reports_copy", { numberOfReports });

            dispatch(
                notificationActions.pushToasterMessage(
                    formatMessage(messages.summarizeCopySuccess),
                    MSGTYPE.SUCCESS
                )
            );
        } catch {
            dispatch(
                notificationActions.pushToasterMessage(
                    formatMessage(messages.summarizeCopyError),
                    MSGTYPE.ERROR
                )
            );
        }
    };

    const getCreditProgressColor = () => {
        if (!creditData) return "green";
        const progress =
            creditData.dailyLimit > 0 ? creditData.currentUsage / creditData.dailyLimit : 0;
        if (!creditData.isAllowed) return "red";
        if (progress >= 0.8) return "orange";
        if (progress >= 0.5) return "yellow";
        return "green";
    };

    const creditFooter = creditData ? (
        <div className="credit-footer">
            <div className="credit-progress-bar">
                <div
                    className={`credit-progress-fill ${getCreditProgressColor()}`}
                    style={{
                        width: `${Math.min(
                            (creditData.currentUsage / creditData.dailyLimit) * 100,
                            100
                        )}%`,
                    }}
                />
            </div>
            <span
                className={`credit-label ${
                    getCreditProgressColor() === "red"
                        ? "red"
                        : getCreditProgressColor() === "orange"
                        ? "orange"
                        : ""
                }`}
            >
                {formatMessage(messages.creditsUsedToday, {
                    current: creditData.currentUsage,
                    limit: creditData.dailyLimit,
                })}
            </span>
        </div>
    ) : null;

    const multiActionList: IMultiActionList[] = [
        {
            action: "Copy",
            actionDisabled: isLoading || !textOutput,
            onAction: handleCopy,
        },
        {
            action: "Print",
            actionDisabled: isLoading || !pdfOutput,
            onAction: handlePrint,
        },
    ];

    return (
        <DialogBox
            isOpen={isOpen}
            onClose={handleClose}
            className="summarize-report-dialog"
            draggable={true}
            unrestricted={true}
            hideCloseX={false}
            footerType={DialogBoxFooterType.MULTI_ACTION}
            multiActionList={multiActionList}
            title={formatMessage(messages.summarizeReportsTitle)}
        >
            <div className="summarize-modal-content">
                <div className={`summarize-loader-wrap${isLoading ? "" : " is-hidden"}`}>
                    <Loader />
                </div>
                <div className={`summarize-output-wrap${isLoading ? " is-hidden" : ""}`}>
                    {textOutput && <MarkdownRenderer markdown={textOutput} />}
                    {errorMessage &&
                        (() => {
                            const { paragraphs, items } = parseErrorMessage(errorMessage);
                            return (
                                <div className="summarize-error-message">
                                    {paragraphs.map((paragraph, i) => (
                                        <p key={i}>{paragraph}</p>
                                    ))}
                                    {items.length > 0 && (
                                        <ul>
                                            {items.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            );
                        })()}
                </div>
            </div>
            <p className="summarize-ai-disclaimer">
                {formatMessage(messages.summarizeAiDisclaimer)}
            </p>
            {creditFooter}
        </DialogBox>
    );
}
